// src/legal/TermsCheckbox.jsx — the terms box (v221): set-password and the purchase confirmation.
//
// Renders the state of useCurrentTerms (`terms` prop = the hook's return value):
//   idle → nothing · loading → a quiet line · failed → red line + «Riprova» (nothing can be submitted) ·
//   ready → the REQUIRED box «Accetto i [Termini di servizio] e ho letto l'[Informativa privacy]», each name a link
//   to that version's own page (+ «(PDF)»), plain text when the link is null (no version configured / not https).
// `changed` → an AMBER note above the box, unless the host words it itself (`showChanged={false}`, /set-password
// uses AuthNotice tone="warning"). `marketing` = { shown, value, onChange }: the OPTIONAL second box (set-password
// only), unticked at first, never blocking. Links open in a new tab, detached. Tokens only (theme.js).
import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { COLORS, FONT_SANS, RADII, SEMANTIC } from '../constants/theme.js';

const ROW = {
  display: 'flex', alignItems: 'flex-start', gap: '8px', margin: '0 0 10px',
  fontSize: '13px', lineHeight: '20px', color: COLORS.ink, fontFamily: FONT_SANS, textAlign: 'left',
};
const BOX = { marginTop: '3px', flex: '0 0 auto', accentColor: COLORS.accent };
const LINK = { color: COLORS.accent, textDecoration: 'underline' };
const QUIET = { ...ROW, color: COLORS.textSecondary };
const note = (pair) => ({
  margin: '0 0 10px', padding: '8px 12px', borderRadius: RADII.control, background: pair.bg,
  border: `1px solid ${pair.fg}`, color: COLORS.ink, fontSize: '13px', lineHeight: '20px',
  fontFamily: FONT_SANS, textAlign: 'left',
});
const RETRY = {
  marginLeft: '8px', padding: 0, border: 'none', background: 'none', color: COLORS.accent,
  textDecoration: 'underline', cursor: 'pointer', fontSize: '13px', fontFamily: FONT_SANS,
};

function DocumentName({ document, label }) {
  if (!document?.url) return <>{label}</>;
  return <a href={document.url} target="_blank" rel="noopener noreferrer" style={LINK}>{label}</a>;
}

function PdfLink({ document, label }) {
  if (!document?.pdfUrl) return null;
  return <> (<a href={document.pdfUrl} target="_blank" rel="noopener noreferrer" style={LINK}>{label}</a>)</>;
}

export default function TermsCheckbox({ terms, disabled = false, marketing = null, showChanged = true }) {
  const { t } = useTranslation('legal');
  const termsId = useId();
  const marketingId = useId();

  if (!terms || terms.status === 'idle') return null;
  if (terms.status === 'loading') return <p style={QUIET}>{t('terms.loading')}</p>;
  if (terms.status === 'failed') {
    return (
      <div role="alert" style={note(SEMANTIC.errore)}>
        {t('terms.loadFailed')}
        <button type="button" onClick={terms.retry} disabled={disabled} style={RETRY}>{t('terms.retry')}</button>
      </div>
    );
  }

  const { tos, privacy } = terms.terms || {};
  return (
    <div data-testid="terms-acceptance">
      {showChanged && terms.changed && <div role="status" style={note(SEMANTIC.attenzione)}>{t('terms.changed')}</div>}
      <div style={ROW}>
        <input id={termsId} type="checkbox" checked={!!terms.accepted} disabled={disabled}
               onChange={(e) => terms.setAccepted(e.target.checked)} style={BOX} />
        <label htmlFor={termsId}>
          {t('terms.before')}
          <DocumentName document={tos} label={t('terms.tos')} />
          <PdfLink document={tos} label={t('terms.pdf')} />
          {t('terms.between')}
          <DocumentName document={privacy} label={t('terms.privacy')} />
          <PdfLink document={privacy} label={t('terms.pdf')} />
        </label>
      </div>
      {marketing?.shown && (
        <div style={ROW}>
          <input id={marketingId} type="checkbox" checked={!!marketing.value} disabled={disabled}
                 onChange={(e) => marketing.onChange(e.target.checked)} style={BOX} />
          <label htmlFor={marketingId}>{t('marketing.label')}</label>
        </div>
      )}
    </div>
  );
}
